import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Package } from 'lucide-react';
import { useOrders } from '../hooks/useOrders.js';
import { useStages } from '../hooks/useStages.js';
import { useWorkspaceStore } from '../stores/workspace.js';
import { useFeatureFlag } from '../lib/posthog.js';
import OrderModal from '../components/orders/OrderModal.js';
import ViewBar from '../components/views/ViewBar.js';
import type { ViewState } from '../components/views/ViewBar.js';
import type { ViewFilter } from '@mayksel/types';
import type { ColumnDef } from '../components/views/ColumnPicker.js';
import type { FieldDef } from '../components/views/FilterPanel.js';

const STATUS_COLORS: Record<string, string> = {
  draft: 'var(--color-text-muted)',
  in_production: 'var(--color-brand)',
  complete: 'var(--color-success)',
  cancelled: 'var(--color-danger)',
};

const PRIORITY_COLORS: Record<string, string> = {
  low: 'var(--color-text-muted)',
  normal: 'var(--color-text-secondary)',
  high: 'var(--color-amber)',
  urgent: 'var(--color-danger)',
};

const ORDER_COLUMNS: ColumnDef[] = [
  { key: 'order_number', label: 'Order #', required: true },
  { key: 'name', label: 'Name', required: true },
  { key: 'status', label: 'Status' },
  { key: 'stage', label: 'Stage' },
  { key: 'priority', label: 'Priority' },
  { key: 'due_date', label: 'Due Date' },
  { key: 'total', label: 'Total' },
];

const DEFAULT_VISIBLE = ORDER_COLUMNS.map((c) => c.key);

/** Convert ViewFilter[] to useOrders params for known fields */
function filtersToApiParams(filters: ViewFilter[]) {
  const params: { status?: string; priority?: string; stageId?: string } = {};
  for (const f of filters) {
    if (f.operator !== 'equals' || f.value === null || f.value === '') continue;
    if (f.field === 'status') params.status = String(f.value);
    if (f.field === 'priority') params.priority = String(f.value);
    if (f.field === 'stage') params.stageId = String(f.value);
  }
  return params;
}

export default function Orders() {
  const { t } = useTranslation('orders');
  const navigate = useNavigate();
  const workspace = useWorkspaceStore((s) => s.workspace);
  const ordersEnabled = useFeatureFlag('orders');

  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [viewState, setViewState] = useState<ViewState>({
    filters: [], columns: DEFAULT_VISIBLE, sort: null, layout: 'table',
  });

  const { data: stagesData } = useStages();
  const stages = stagesData ?? [];

  const apiParams = useMemo(() => {
    const fromFilters = filtersToApiParams(viewState.filters);
    const opts: Parameters<typeof useOrders>[0] = { limit: 100, ...fromFilters };
    if (search) opts.search = search;
    if (viewState.sort) {
      opts.sortBy = viewState.sort.field;
      opts.sortDir = viewState.sort.direction;
    }
    return opts;
  }, [viewState.filters, viewState.sort, search]);

  const { data, isLoading, isError } = useOrders(apiParams);

  const orderLabel = workspace?.orderLabel ?? 'Order';
  const orderLabelPlural = workspace?.orderLabelPlural ?? 'Orders';
  const currency = workspace?.currency ?? 'USD';

  const orders = data?.data ?? [];
  const visibleColumns = viewState.columns.length > 0 ? viewState.columns : DEFAULT_VISIBLE;

  // Build column grid template from visible columns
  const COL_WIDTHS: Record<string, string> = {
    order_number: '120px',
    name: '1fr',
    status: '140px',
    stage: '140px',
    priority: '100px',
    due_date: '110px',
    total: '100px',
  };
  const gridCols = visibleColumns.map((k) => COL_WIDTHS[k] ?? '1fr').join(' ');

  const ORDER_FIELD_DEFS: FieldDef[] = [
    { key: 'status', label: t('columns.status'), type: 'select', options: [
      { value: 'draft', label: t('status.draft') },
      { value: 'in_production', label: t('status.in_production') },
      { value: 'complete', label: t('status.complete') },
      { value: 'cancelled', label: t('status.cancelled') },
    ]},
    { key: 'priority', label: t('columns.priority'), type: 'select', options: [
      { value: 'low', label: t('priority.low') },
      { value: 'normal', label: t('priority.normal') },
      { value: 'high', label: t('priority.high') },
      { value: 'urgent', label: t('priority.urgent') },
    ]},
    { key: 'stage', label: t('columns.stage'), type: 'select',
      options: stages.map((s) => ({ value: s.id, label: s.name })) },
    { key: 'due_date', label: t('columns.due_date'), type: 'date' },
  ];

  if (!ordersEnabled) return null;

  return (
    <div className="page-enter">
      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-4)' }}>
        <h1 className="page-title">{orderLabelPlural}</h1>
        <button
          onClick={() => setCreateOpen(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
            padding: 'var(--space-2) var(--space-4)', background: 'var(--color-brand)',
            color: 'var(--color-text-inverse)', border: 'none',
            borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-medium)', cursor: 'pointer',
          }}
        >
          <Plus size={16} />
          {t('new_order', { label: orderLabel })}
        </button>
      </div>

      {/* Toolbar: search + ViewBar */}
      <div style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-5)', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text" value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('search_placeholder', { label_plural: orderLabelPlural })}
          style={{
            flex: '0 0 260px', padding: 'var(--space-2) var(--space-3)',
            border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
            fontSize: 'var(--text-sm)', color: 'var(--color-text)', background: 'var(--color-bg)',
          }}
        />
        <ViewBar
          entityType="order"
          columnDefs={ORDER_COLUMNS}
          fieldDefs={ORDER_FIELD_DEFS}
          onViewChange={setViewState}
        />
      </div>

      {/* Table */}
      {isError ? (
        <div style={{ textAlign: 'center', padding: 'var(--space-16)' }}>
          <p style={{ color: 'var(--color-danger)', fontSize: 'var(--text-sm)' }}>
            {t('load_error')}
          </p>
        </div>
      ) : isLoading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="skeleton-line" style={{ height: 40 }} />
          ))}
        </div>
      ) : orders.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 'var(--space-16)' }}>
          <Package size={48} style={{ color: 'var(--color-gray-300)', marginBottom: 'var(--space-4)' }} />
          <p style={{ fontWeight: 'var(--font-medium)', color: 'var(--color-text)', marginBottom: 'var(--space-2)' }}>
            {t('empty_title', { label_plural: orderLabelPlural })}
          </p>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            {t('empty_description', { label: orderLabel })}
          </p>
        </div>
      ) : (
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          {/* Table header */}
          <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 'var(--space-4)', padding: 'var(--space-3) var(--space-4)', background: 'var(--color-bg-subtle)', borderBottom: '1px solid var(--color-border)' }}>
            {visibleColumns.map((key) => {
              const col = ORDER_COLUMNS.find((c) => c.key === key);
              return (
                <span key={key} style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {col?.label ?? key}
                </span>
              );
            })}
          </div>

          {orders.map((order, idx) => {
            const statusColor = STATUS_COLORS[order.status] ?? 'var(--color-text-muted)';
            const priorityColor = PRIORITY_COLORS[order.priority] ?? 'var(--color-text-secondary)';

            return (
              <button
                key={order.id}
                onClick={() => navigate(`/orders/${order.id}`)}
                style={{
                  display: 'grid', gridTemplateColumns: gridCols,
                  gap: 'var(--space-4)', padding: 'var(--space-3) var(--space-4)',
                  width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer',
                  borderBottom: idx < orders.length - 1 ? '1px solid var(--color-gray-100)' : 'none',
                  alignItems: 'center',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-bg-subtle)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
              >
                {visibleColumns.map((key) => {
                  switch (key) {
                    case 'order_number':
                      return <span key={key} style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-mono, monospace)' }}>{order.orderNumber}</span>;
                    case 'name':
                      return <span key={key} style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)', fontWeight: 'var(--font-medium)' }}>{order.name}</span>;
                    case 'status':
                      return (
                        <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--text-sm)', color: statusColor }}>
                          <span style={{ width: 7, height: 7, borderRadius: '50%', background: statusColor, flexShrink: 0 }} />
                          {t(`status.${order.status}`)}
                        </span>
                      );
                    case 'stage':
                      return (
                        <span key={key} style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                          {order.stageName ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                              <span style={{ width: 7, height: 7, borderRadius: '50%', background: order.stageColor ?? 'var(--color-gray-400)', flexShrink: 0 }} />
                              {order.stageName}
                            </span>
                          ) : '—'}
                        </span>
                      );
                    case 'priority':
                      return <span key={key} style={{ fontSize: 'var(--text-sm)', color: priorityColor, fontWeight: order.priority === 'urgent' ? 'var(--font-medium)' : undefined }}>{t(`priority.${order.priority}`)}</span>;
                    case 'due_date':
                      return <span key={key} style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>{order.dueDate ?? '—'}</span>;
                    case 'total':
                      return <span key={key} style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>{order.total !== '0' ? `${currency} ${parseFloat(order.total).toLocaleString()}` : '—'}</span>;
                    default:
                      return <span key={key} />;
                  }
                })}
              </button>
            );
          })}
        </div>
      )}

      {createOpen && (
        <OrderModal onClose={() => setCreateOpen(false)} />
      )}
    </div>
  );
}
